import { useState, useEffect, useRef, useCallback } from 'react';
import { X, Send, Loader2 } from 'lucide-react';
import api from '@/utils/api';

const SARAH_IMG = '/images/sarah.jpg';
const GREETING = "Hi! I'm Sarah, your AI assistant. Are you looking for workers' comp coverage for your California business? What industry are you in? I'm not a licensed agent.";

export default function SarahChat() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `session-${Date.now()}-${Math.random().toString(36).slice(2)}`);
  const exitTriggeredRef = useRef(false);
  const scrollRef = useRef(null);

  // Exit intent removed — Sarah only opens when clicked
  // No auto-popup on mouse leave or scroll

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const openChat = useCallback(() => {
    setIsOpen(true);
    if (messages.length === 0) setMessages([{ role: 'assistant', content: GREETING }]);
  }, [messages.length]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setLoading(true);
    try {
      const { data } = await api.post('/chat', { session_id: sessionId, message: userMsg });
      setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: "I'd be happy to help you find workers comp coverage. What industry is your business in?" }]);
    }
    setLoading(false);
  };

  return (
    <>
      {/* Chat bubble with Sarah's photo + pulse */}
      {!isOpen && (
        <button onClick={openChat} className="fixed bottom-[70px] right-6 z-40 group" aria-label="Chat with Sarah" data-testid="sarah-chat-bubble">
          <div className="absolute inset-0 bg-red-500 rounded-full animate-ping opacity-30" />
          <div className="relative w-16 h-16 rounded-full shadow-xl border-2 border-white overflow-hidden hover:scale-110 transition-transform">
            <img src={SARAH_IMG} alt="Chat with Sarah" className="w-full h-full object-cover" />
          </div>
        </button>
      )}

      {/* Chat panel */}
      {isOpen && (
        <div className="fixed bottom-[60px] right-4 z-50 w-[370px] max-w-[calc(100vw-2rem)] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden" style={{ height: '500px' }} data-testid="sarah-chat-panel">
          {/* Header with Sarah's photo */}
          <div className="bg-cwc-navy text-white p-4 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-white/30 shrink-0">
                <img src={SARAH_IMG} alt="Sarah" className="w-full h-full object-cover" />
              </div>
              <div>
                <p className="font-semibold text-sm">Sarah</p>
                <p className="text-[10px] text-white/50">AI Workers Comp Assistant</p>
              </div>
            </div>
            <button onClick={() => setIsOpen(false)} className="text-white/60 hover:text-white" aria-label="Close chat" data-testid="sarah-chat-close"><X className="w-5 h-5" /></button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="sarah-chat-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start gap-2'}`}>
                {m.role === 'assistant' && (
                  <div className="w-7 h-7 rounded-full overflow-hidden shrink-0 mt-1">
                    <img src={SARAH_IMG} alt="Sarah" className="w-full h-full object-cover" />
                  </div>
                )}
                <div className={`max-w-[78%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${m.role === 'user' ? 'bg-cwc-navy text-white rounded-br-sm' : 'bg-gray-100 text-gray-800 rounded-bl-sm'}`}>
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start gap-2">
                <div className="w-7 h-7 rounded-full overflow-hidden shrink-0 mt-1">
                  <img src={SARAH_IMG} alt="Sarah" className="w-full h-full object-cover" />
                </div>
                <div className="bg-gray-100 px-4 py-2.5 rounded-2xl rounded-bl-sm">
                  <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                </div>
              </div>
            )}
          </div>

          {/* AI disclosure */}
          <p className="text-[9px] text-gray-400 text-center px-4 shrink-0">Sarah is an AI assistant, not a licensed agent. For licensed advice, a real agent will follow up.</p>

          {/* Input */}
          <div className="p-3 border-t border-gray-100 shrink-0">
            <div className="flex gap-2">
              <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && sendMessage()} placeholder="Type a message..." className="flex-1 px-3 py-2 rounded-full border border-gray-200 text-sm focus:ring-2 focus:ring-red-400/50 focus:border-red-400 outline-none" data-testid="sarah-chat-input" />
              <button onClick={sendMessage} disabled={loading || !input.trim()} className="bg-red-600 text-white w-10 h-10 rounded-full flex items-center justify-center hover:bg-red-700 disabled:opacity-50 transition-colors" data-testid="sarah-chat-send"><Send className="w-4 h-4" /></button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
